// eslint-disable-next-line no-unused-vars
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Loading } from '@carbon/react';
import { oneOf, string } from 'prop-types';

import useFetch from 'Hooks/useFetch';
import useAnalytics from 'Hooks/useAnalytics';
import useAsyncError from 'Hooks/useAsyncError';
import { API_EU_FUNDS } from 'Constants';

const StyledPortalButton = styled.div`
    display: inline-flex;
    max-width: 100%;
    flex-direction: column;
    align-items: flex-start;

    &.fullWidth {
        display: flex;
        width: 100%;
    }

    .portalButton {
        display: flex;
        align-items: center;
        max-width: 100%;
        border: 0;
        font-family: inherit;
        font-weight: 600;
        line-height: 1.2;
        cursor: pointer;
    }

    &.fullWidth .portalButton {
        width: 100%;
    }

    .portalButton:disabled {
        cursor: wait;
        opacity: 0.65;
    }

    .portalButton:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
`;

const PortalButtonComponent = ({
  label,
  backgroundColor,
  textColor,
  fontSize,
  paddingVertical,
  paddingHorizontal,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  borderRadius,
  width,
}) => {
  const fetcher = useFetch();
  const analytics = useAnalytics();
  const throwError = useAsyncError();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) return;

    setLoading(true);

    try {
      analytics.track('eu_funds_platform_access');
      const { url } = await fetcher(API_EU_FUNDS, { method: 'POST' });
      window.open(url, '_blank');
    } catch (requestError) {
      throwError(requestError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledPortalButton
      className={ width === 'full' ? 'fullWidth' : '' }
      style={ {
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
      } }
    >
      <button
        className="portalButton"
        disabled={ loading }
        type="button"
        style={ {
          backgroundColor,
          borderRadius,
          color: textColor,
          fontSize,
          padding: `${paddingVertical} ${paddingHorizontal}`,
        } }
        onClick={ handleClick }
      >
        {loading && <Loading active small withOverlay={ false } />}
        {label}
      </button>
    </StyledPortalButton>
  );
};

PortalButtonComponent.propTypes = {
  label: string,
  backgroundColor: string,
  textColor: string,
  fontSize: string,
  paddingVertical: string,
  paddingHorizontal: string,
  marginTop: string,
  marginRight: string,
  marginBottom: string,
  marginLeft: string,
  borderRadius: string,
  width: oneOf(['auto', 'full']),
};

const PortalButton = {
  label: 'EU Funds Button',
  fields: {
    label: { type: 'text', label: 'Button label' },
    backgroundColor: { type: 'text', label: 'Background color' },
    textColor: { type: 'text', label: 'Text color' },
    fontSize: { type: 'text', label: 'Font size' },
    paddingVertical: { type: 'text', label: 'Vertical padding' },
    paddingHorizontal: { type: 'text', label: 'Horizontal padding' },
    marginTop: { type: 'text', label: 'Margin top' },
    marginRight: { type: 'text', label: 'Margin right' },
    marginBottom: { type: 'text', label: 'Margin bottom' },
    marginLeft: { type: 'text', label: 'Margin left' },
    borderRadius: { type: 'text', label: 'Corner radius' },
    width: {
      type: 'radio',
      label: 'Width',
      options: [
        { label: 'Fit content', value: 'auto' },
        { label: 'Full width', value: 'full' },
      ],
    },
  },
  defaultProps: {
    label: 'Open Funds Portal',
    backgroundColor: '#f47926',
    textColor: '#ffffff',
    fontSize: '14px',
    paddingVertical: '8px',
    paddingHorizontal: '32px',
    marginTop: '0',
    marginRight: '0',
    marginBottom: '0',
    marginLeft: '0',
    borderRadius: '2px',
    width: 'auto',
  },
  // eslint-disable-next-line react/jsx-props-no-spreading
  render: props => <PortalButtonComponent { ...props } />,
};

// eslint-disable-next-line import/prefer-default-export
export { PortalButton };
